<template>
  <Transition name="book-bound-toast">
    <div v-if="visible" class="book-bound-toast" role="status" aria-live="polite">
      <span class="book-bound-toast__text">{{ message }}</span>
    </div>
  </Transition>
</template>

<script setup lang="ts">
  import { onBeforeUnmount, ref } from 'vue';

  const DURATION_MS = 1800;

  const visible = ref(false);
  const message = ref('');
  let hideTimer: number | null = null;

  /** 清除自动隐藏计时器。 */
  const clearHideTimer = () => {
    if (hideTimer === null) return;
    window.clearTimeout(hideTimer);
    hideTimer = null;
  };

  /**
   * 展示边界提示。
   * 步骤1：写入文案；步骤2：显示；步骤3：定时自动隐藏。
   */
  const show = (text: string, _flipDirection?: 'forward' | 'back') => {
    clearHideTimer();
    message.value = text;
    visible.value = true;
    hideTimer = window.setTimeout(() => {
      visible.value = false;
      hideTimer = null;
    }, DURATION_MS);
  };

  onBeforeUnmount(clearHideTimer);

  defineExpose({ show });
</script>

<style scoped>
  .book-bound-toast {
    --book-bound-toast-ink: #f7f1e8;
    --book-bound-toast-glass: rgba(22, 16, 28, 0.72);
    --book-bound-toast-rim: rgba(247, 241, 232, 0.16);

    position: absolute;
    top: max(18px, calc(env(safe-area-inset-top, 0px) + 14px));
    left: 50%;
    z-index: 1200;
    display: inline-flex;
    align-items: center;
    max-width: min(86%, 320px);
    padding: 11px 18px;
    box-sizing: border-box;
    color: var(--book-bound-toast-ink);
    background:
      linear-gradient(135deg, rgba(255, 255, 255, 0.08), transparent 42%),
      var(--book-bound-toast-glass);
    border: 1px solid var(--book-bound-toast-rim);
    border-radius: 999px;
    box-shadow:
      0 10px 28px rgba(10, 6, 16, 0.28),
      inset 0 1px 0 rgba(255, 255, 255, 0.12);
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
    pointer-events: none;
    transform: translateX(-50%);
  }

  .book-bound-toast__text {
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'PingFang SC', 'Microsoft YaHei', sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.35;
    letter-spacing: 0.04em;
    white-space: nowrap;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.28);
  }

  .book-bound-toast-enter-active {
    transition:
      opacity 220ms ease-out,
      transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
  }

  .book-bound-toast-leave-active {
    transition:
      opacity 160ms ease-in,
      transform 160ms ease-in;
  }

  .book-bound-toast-enter-from {
    opacity: 0;
    transform: translateX(-50%) translateY(-10px) scale(0.96);
  }

  .book-bound-toast-leave-to {
    opacity: 0;
    transform: translateX(-50%) translateY(-6px) scale(0.98);
  }

  @media (prefers-reduced-motion: reduce) {
    .book-bound-toast-enter-active,
    .book-bound-toast-leave-active {
      transition: opacity 120ms ease;
    }

    .book-bound-toast-enter-from,
    .book-bound-toast-leave-to {
      transform: translateX(-50%);
    }
  }
</style>
